// Shows "Processing..." on the button that started a request. The label is
// drawn by CSS (index.css) over the button: never change the button's own
// children - React owns them, and when it next updates a button whose
// children were swapped out it throws and blanks the whole page.
const BUTTON_ATTR = "data-processing-active";
const BUTTON_ORIGINAL_DISABLED_ATTR = "data-processing-original-disabled";
const RELATIVE_ATTR = "data-processing-relative";
const COMPACT_ATTR = "data-processing-compact";
// Narrower than this and "Processing..." doesn't fit - show just the spinner.
const COMPACT_WIDTH_PX = 120;
const COLOR_VAR = "--processing-color";
const CANDIDATE_TTL_MS = 2000;

let initialized = false;
let pendingRequests = 0;
let candidateButton = null;
let candidateTimestamp = 0;
const activeButtons = new Set();

function captureCandidate(button) {
  if (!button || button.disabled || button.dataset.processingIgnore === "true") {
    return;
  }
  candidateButton = button;
  candidateTimestamp = Date.now();
}

function activateButton(button) {
  if (!button || !button.isConnected || activeButtons.has(button)) {
    return;
  }

  // The CSS hides the real label with a transparent color, so keep the
  // button's own color for the overlay.
  const computed = window.getComputedStyle(button);
  button.style.setProperty(COLOR_VAR, computed.color);
  // The overlay is positioned against the button; leave buttons that are
  // already positioned (absolute close buttons and the like) as they are.
  if (computed.position === "static") button.setAttribute(RELATIVE_ATTR, "true");
  if (button.offsetWidth < COMPACT_WIDTH_PX) button.setAttribute(COMPACT_ATTR, "true");
  button.setAttribute(BUTTON_ORIGINAL_DISABLED_ATTR, button.disabled ? "true" : "false");
  button.setAttribute(BUTTON_ATTR, "true");
  button.setAttribute("aria-busy", "true");
  button.disabled = true;
  activeButtons.add(button);
}

function restoreButtons() {
  activeButtons.forEach((button) => {
    if (!button || !button.isConnected) {
      return;
    }

    const wasDisabled = button.getAttribute(BUTTON_ORIGINAL_DISABLED_ATTR) === "true";

    button.disabled = wasDisabled;
    button.removeAttribute(BUTTON_ATTR);
    button.removeAttribute(BUTTON_ORIGINAL_DISABLED_ATTR);
    button.removeAttribute("aria-busy");
    button.removeAttribute(RELATIVE_ATTR);
    button.removeAttribute(COMPACT_ATTR);
    button.style.removeProperty(COLOR_VAR);
  });

  activeButtons.clear();
  candidateButton = null;
  candidateTimestamp = 0;
}

function beginRequest() {
  pendingRequests += 1;
  if (pendingRequests !== 1) {
    return;
  }

  if (
    candidateButton &&
    Date.now() - candidateTimestamp <= CANDIDATE_TTL_MS &&
    candidateButton.isConnected
  ) {
    activateButton(candidateButton);
  }
}

function endRequest() {
  pendingRequests = Math.max(0, pendingRequests - 1);
  if (pendingRequests === 0) {
    restoreButtons();
  }
}

function patchFetch() {
  if (typeof window.fetch !== "function") {
    return;
  }

  const originalFetch = window.fetch.bind(window);
  window.fetch = (...args) => {
    beginRequest();
    return originalFetch(...args).finally(() => {
      endRequest();
    });
  };
}

function patchXhr() {
  if (!window.XMLHttpRequest) {
    return;
  }

  const originalSend = window.XMLHttpRequest.prototype.send;
  window.XMLHttpRequest.prototype.send = function patchedSend(...args) {
    beginRequest();
    this.addEventListener(
      "loadend",
      () => {
        endRequest();
      },
      { once: true }
    );

    return originalSend.apply(this, args);
  };
}

function handleClick(event) {
  const button = event.target && event.target.closest ? event.target.closest("button") : null;
  captureCandidate(button);
}

function handleSubmit(event) {
  const submitter = event.submitter && event.submitter.tagName === "BUTTON" ? event.submitter : null;
  captureCandidate(submitter);
}

export function initGlobalButtonProcessing() {
  if (initialized || typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  patchFetch();
  patchXhr();

  document.addEventListener("click", handleClick, true);
  document.addEventListener("submit", handleSubmit, true);
  initialized = true;
}
